import React, { createRef, useEffect } from 'react'
import { EditProductPageProps } from './EditProductPage.types'
import styles from './EditProductPage.module.scss'
import cl from 'classnames'
import { inject } from 'mobx-react'
import { GENERAL_STORE, PRODUCT_SKU_STORE, PRODUCT_STORE, ROUTING_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import { IconProducts, IconViewers } from 'assets'
import {
  Button, EntityInfoSectionLayout, GalleryDropZone, GalleryDropZoneRef, H2, Input, LabeledLine,
  NoneLabel, PageHeader, SectionContainer, TagsEditor, Textarea
} from 'components/shared'
import { ShippingOptionsTable } from './ShippingOptionsTable'
import { ProductSkusTable } from './ProductSkusTable'

const EditProductPageComponent = (props: EditProductPageProps) => {
  const {
    match: { params: { id } },
    [PRODUCT_STORE]: {
      getProductById,
      productToView: product,
      setProductProps,
      getBrandShippings,
      hasBrandShippings,
      editProduct,
      productHasChangesToSave,
      setHasChangesToSave
    },
    [ROUTING_STORE]: {
      push
    },
    [GENERAL_STORE]: {
      setLoadingOn,
      showError
    },
    [PRODUCT_SKU_STORE]: {
      hasSkus,
      getSearchedProductSkus
    }
  } = props

  useEffect(() => {
    getProductById(id)
      .then(product => {
        if (product) {
          getBrandShippings(product.brandId)
          getSearchedProductSkus()
        }
        setHasChangesToSave(false)
      })
  }, [getProductById, id, getBrandShippings, setHasChangesToSave, getSearchedProductSkus])

  const imagesRef: GalleryDropZoneRef = createRef()

  const handleDiscardChangesClick = () => {
    getProductById(id)
      .then(() => {
        setHasChangesToSave(false)
      })
  }

  const handleSaveChanges = () => {
    setLoadingOn()
    imagesRef.current.handleImages()
      .then(() => {
        setProductProps({ images: imagesRef.current.getImages() })
        editProduct()
      })
      .catch(err => showError(err.message))
  }

  return (
    <div className={cl(styles.container, styles.scrollable)} style={ { overflowX: 'hidden' } }>

      <PageHeader
        navSegments={[
          {
            icon: IconProducts,
            label: 'Products',
            onClick: () => push('/products')
          },
          {
            label: product.name
          }
        ]}
        buttons={[
          {
            preset: 'secondary',
            disabled: !productHasChangesToSave,
            onClick: handleDiscardChangesClick,
            children: 'Discard'
          },
          {
            preset: 'primary',
            disabled: !productHasChangesToSave,
            onClick: handleSaveChanges,
            children: 'Save'
          }
        ]}
      />

      <SectionContainer className={styles.section} extraPaddingBottom>
        <H2 className={styles.sectionHeadingWithButton}>
          Product Details
          <Button
            preset='icon'
            className={styles.headingButton}
            disabled
          >
            <IconViewers />
            View in marketplace
          </Button>
        </H2>

        <EntityInfoSectionLayout
          leftContainerNode={<>
            <LabeledLine
              label='Product name'
              style={ { marginBottom: 10 } }
            >
              <Input
                placeholder='Product name'
                value={product.name || ''}
                onChange={name => setProductProps({ name })}
              />
            </LabeledLine>
            <LabeledLine
              label='Tags'
              preset='tags'
            >
              <TagsEditor
                editMode
                tags={product.tags}
                onChange={tags => setProductProps({ tags })}
              />
            </LabeledLine>
          </>}
          rightContainerNode={(
            <LabeledLine
              label='Description'
              style={{ width: '100%', alignItems: 'stretch' }}
              alignItems='flexStart'
            >
              <Textarea
                className={styles.textarea}
                value={product.info}
                onChange={info => setProductProps({ info })}
              />
            </LabeledLine>
          )}
        />

      </SectionContainer>

      <SectionContainer className={styles.section} extraPaddingBottom={hasBrandShippings}>
        <H2>Shipping options</H2>
        {hasBrandShippings
          ? <ShippingOptionsTable />
          : <NoneLabel />
        }
      </SectionContainer>

      <SectionContainer
        className={styles.section}
        extraPaddingBottom
        onClick={() => setHasChangesToSave(true)}
      >
        <H2>Image gallery</H2>
        <GalleryDropZone
          key={product.id}
          ref={imagesRef}
          initialImages={product.images || []}
        />
      </SectionContainer>

      <SectionContainer className={styles.section} extraPaddingBottom={hasSkus}>
        <H2>Products SKUs</H2>
        {hasSkus
          ? <ProductSkusTable />
          : <NoneLabel />
        }
      </SectionContainer>
    </div>
  )
}

const EditProductPage = inject(
  PRODUCT_STORE, ROUTING_STORE, GENERAL_STORE, PRODUCT_SKU_STORE
)(observer(EditProductPageComponent))
export { EditProductPage }
